import styled from 'styled-components';
import { useState } from 'react';
import { s } from './NPL.styles';
import { ReportFormatNumber } from '../../../../func/Function';
import { NplCalculation } from '../../../../utils/nplAnalysis';

interface ICostAnalysisProps {
  profitAnalysis: ReturnType<NplCalculation['getProfitAnalysis']>;
}

const ToggleSection = ({
  label,
  amount,
  isOpen,
  onToggle,
  children,
}: {
  label: React.ReactNode;
  amount?: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}) => (
  <Toggle>
    <RowButton type="button" onClick={onToggle} aria-expanded={isOpen}>
      <RowLabel>{label}</RowLabel>
      <RowRight>
        {amount != null && <RowAmount>{amount}</RowAmount>}
        <Arrow
          src={
            isOpen
              ? '/assets/images/arrow/black_arrow_up_20.svg'
              : '/assets/images/arrow/black_arrow_down_20.svg'
          }
          alt=""
        />
      </RowRight>
    </RowButton>
    {isOpen && <ExpandBox>{children}</ExpandBox>}
  </Toggle>
);

/** 펼침 영역 공통 UI */
const EBList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const EBRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: baseline;
  column-gap: 12px;
`;
const EBLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: rgba(55, 56, 60, 0.61);
`;
const EBValue = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #616161;
  text-align: right;
`;
const EBSubLeft = styled.div`
  grid-column: 1 / 2;
  font-size: 12px;
  color: rgba(55, 56, 60, 0.61);
  margin-top: 4px;
`;
const EBSubRight = styled.div`
  grid-column: 2 / 3;
  justify-self: end;
  font-size: 11px;
  color: rgba(55, 56, 60, 0.61);
  margin-top: 4px;
`;

const CostAnalysis = (props: ICostAnalysisProps) => {
  const { profitAnalysis } = props;

  // A안 토글 상태
  const [openPurchaseA, setOpenPurchaseA] = useState(false);
  const [openExtraA, setOpenExtraA] = useState(false);
  const [openAuctionA, setOpenAuctionA] = useState(false);

  // B안 토글 상태
  const [openPurchaseB, setOpenPurchaseB] = useState(false);
  const [openPledgeB, setOpenPledgeB] = useState(false);
  const [openExtraB, setOpenExtraB] = useState(false);
  const [openAuctionB, setOpenAuctionB] = useState(false);

  const safeNum = (v: unknown) =>
    typeof v === 'number' && isFinite(v) ? v : 0;

  const amountExtraB =
    -safeNum(profitAnalysis.row2_2) + // 질권 대출(음수 처리)
    safeNum(profitAnalysis.row4_3) + // 이자비용(+)
    safeNum(profitAnalysis.row6_2); // 취급수수료(+)

  const feePurchase = safeNum(profitAnalysis.row5_1); // 채권 매입금액
  const feeStamp = safeNum(profitAnalysis.row5_2); // 질권인지세
  const feeTransfer = safeNum(profitAnalysis.row7_1); // 근저당권 이전수수료
  const feeTrade = Math.round(feePurchase * 0.015); // 거래수수료(1.5%) - 화면 표시와 동일한 반올림

  // A안 부대비용 소계
  const extraSubtotalA = feeStamp + feeTrade + feeTransfer;

  // B안 부대비용 소계 (필요 시 A와 동일 계산 사용)
  const extraSubtotalB = feeStamp + feeTrade + feeTransfer;

  return (
    <s.Div>
      <s.Title>비용분석</s.Title>

      {/* A안 */}
      <Subdiv>
        <Subtitle>
          <img
            src="/assets/images/icon/icon_money_20.svg"
            style={{ width: 20, height: 20 }}
            alt=""
          />
          <span>A안 자기자본 투자비용</span>
        </Subtitle>

        <s.GrayBox style={{ marginTop: 16 }}>
          <s.Item style={{ padding: '6px 0' }}>
            <s.ItemLabel>총 투자비용</s.ItemLabel>
            <s.ItemContent>
              {ReportFormatNumber(profitAnalysis.row10_1)}
            </s.ItemContent>
          </s.Item>
        </s.GrayBox>

        <ToggleList>
          <ToggleSection
            label="채권 매입금액"
            amount={ReportFormatNumber(profitAnalysis.row5_1)}
            isOpen={openPurchaseA}
            onToggle={() => setOpenPurchaseA((v) => !v)}
          >
            <EBList>
              <EBRow>
                <EBLabel>채권 최고액</EBLabel>
                <EBValue>{ReportFormatNumber(profitAnalysis.row1_1)}</EBValue>
              </EBRow>

              <EBRow>
                <EBLabel>대출원금 (예상)</EBLabel>
                <EBValue>{ReportFormatNumber(profitAnalysis.row2_1)}</EBValue>
                <EBSubRight>채권최고액 / 120%</EBSubRight>
              </EBRow>

              <EBRow>
                <EBLabel>대출잔액 + 미수이자 (예상)</EBLabel>
                <EBValue>{ReportFormatNumber(profitAnalysis.row3_1)}</EBValue>
              </EBRow>

              <EBRow>
                <EBLabel
                  style={{
                    color: profitAnalysis.row4_2
                      ? '#008AFF'
                      : 'rgba(55, 56, 60, 0.61)',
                  }}
                >
                  부실채권 할인금액
                </EBLabel>

                <EBValue
                  style={{
                    color: profitAnalysis.row4_2
                      ? '#008AFF'
                      : 'rgba(55, 56, 60, 0.61)',
                  }}
                >
                  {ReportFormatNumber(profitAnalysis.row4_2)}
                </EBValue>

                <EBSubLeft
                  style={{
                    color:
                      profitAnalysis.row4_2 && profitAnalysis.row2_1
                        ? '#222222'
                        : 'rgba(55, 56, 60, 0.61)',
                  }}
                >
                  수익확보를 위한 할인금액
                </EBSubLeft>

                {/* 퍼센트 계산 값이 존재할 때만 렌더링 */}
                {profitAnalysis.row4_2 && profitAnalysis.row2_1 && (
                  <EBSubRight style={{ color: '#222222' }}>
                    대출원금의{' '}
                    {ReportFormatNumber(
                      (profitAnalysis.row4_2 / profitAnalysis.row2_1) * 100,
                      true,
                    )}{' '}
                    할인
                  </EBSubRight>
                )}
              </EBRow>
            </EBList>
          </ToggleSection>

          <ToggleSection
            label="부대비용 소계"
            amount={ReportFormatNumber(extraSubtotalA)}
            isOpen={openExtraA}
            onToggle={() => setOpenExtraA((v) => !v)}
          >
            <EBList>
              <EBRow>
                <EBLabel>질권인지세</EBLabel>
                <EBValue>{ReportFormatNumber(profitAnalysis.row5_2)}</EBValue>
              </EBRow>

              <EBRow>
                <EBLabel>거래수수료</EBLabel>
                <EBValue>
                  {ReportFormatNumber(
                    Math.round(profitAnalysis.row5_1 * 0.015),
                  )}
                </EBValue>
                <EBSubRight>채권 매입금액의 1.5%</EBSubRight>
              </EBRow>

              <EBRow>
                <EBLabel>근저당권 이전수수료</EBLabel>
                <EBValue>{ReportFormatNumber(profitAnalysis.row7_1)}</EBValue>
                <EBSubRight>채권 매입금액의 0.5%</EBSubRight>
              </EBRow>
            </EBList>
          </ToggleSection>

          <ToggleSection
            label="경매비용"
            amount={ReportFormatNumber(profitAnalysis.row9)}
            isOpen={openAuctionA}
            onToggle={() => setOpenAuctionA((v) => !v)}
          >
            <EBList>
              <EBRow>
                <EBLabel>경매비용</EBLabel>
                <EBValue>{ReportFormatNumber(profitAnalysis.row9)}</EBValue>
                <EBSubRight>예상 낙찰가의 0.5%</EBSubRight>
              </EBRow>
            </EBList>
          </ToggleSection>
        </ToggleList>
      </Subdiv>

      {/* B안 */}
      <Subdiv>
        <Subtitle>
          <img
            src="/assets/images/icon/graph_icon_20.svg"
            style={{ width: 20, height: 20 }}
            alt=""
          />
          <span>B안 레버리지 투자비용</span>
        </Subtitle>

        <s.GrayBox style={{ marginTop: 16 }}>
          <s.Item style={{ padding: '6px 0' }}>
            <s.ItemLabel>총 투자비용</s.ItemLabel>
            <s.ItemContent>
              {ReportFormatNumber(profitAnalysis.row10_2 - 175000)}
            </s.ItemContent>
          </s.Item>
        </s.GrayBox>

        <ToggleList>
          <ToggleSection
            label="채권 매입금액"
            amount={ReportFormatNumber(profitAnalysis.row5_1)}
            isOpen={openPurchaseB}
            onToggle={() => setOpenPurchaseB((v) => !v)}
          >
            <EBList>
              <EBRow>
                <EBLabel>채권 최고액</EBLabel>
                <EBValue>{ReportFormatNumber(profitAnalysis.row1_1)}</EBValue>
              </EBRow>

              <EBRow>
                <EBLabel>대출원금 (예상)</EBLabel>
                <EBValue>{ReportFormatNumber(profitAnalysis.row2_1)}</EBValue>
                <EBSubRight>채권최고액 / 120%</EBSubRight>
              </EBRow>

              <EBRow>
                <EBLabel>대출잔액 + 미수이자 (예상)</EBLabel>
                <EBValue>{ReportFormatNumber(profitAnalysis.row3_1)}</EBValue>
              </EBRow>

              <EBRow>
                <EBLabel
                  style={{
                    color: profitAnalysis.row4_2
                      ? '#008AFF'
                      : 'rgba(55, 56, 60, 0.61)',
                  }}
                >
                  부실채권 할인금액
                </EBLabel>

                <EBValue
                  style={{
                    color: profitAnalysis.row4_2
                      ? '#008AFF'
                      : 'rgba(55, 56, 60, 0.61)',
                  }}
                >
                  {ReportFormatNumber(profitAnalysis.row4_2)}
                </EBValue>

                <EBSubLeft
                  style={{
                    color:
                      profitAnalysis.row4_2 && profitAnalysis.row2_1
                        ? '#222222'
                        : 'rgba(55, 56, 60, 0.61)',
                  }}
                >
                  수익확보를 위한 할인금액
                </EBSubLeft>

                {/* 퍼센트 계산 값이 존재할 때만 렌더링 */}
                {profitAnalysis.row4_2 && profitAnalysis.row2_1 && (
                  <EBSubRight style={{ color: '#222222' }}>
                    대출원금의{' '}
                    {ReportFormatNumber(
                      (profitAnalysis.row4_2 / profitAnalysis.row2_1) * 100,
                      true,
                    )}{' '}
                    할인
                  </EBSubRight>
                )}
              </EBRow>
            </EBList>
          </ToggleSection>

          <ToggleSection
            label="질권 대출 및 비용"
            amount={ReportFormatNumber(Math.abs(amountExtraB))}
            isOpen={openPledgeB}
            onToggle={() => setOpenPledgeB((v) => !v)}
          >
            <EBList>
              <EBRow>
                <EBLabel>질권 (LTV)</EBLabel>
                <EBValue>
                  {ReportFormatNumber(profitAnalysis.row1_2, true, true)}
                </EBValue>
              </EBRow>

              <EBRow>
                <EBLabel>질권 이자율</EBLabel>
                <EBValue>
                  {ReportFormatNumber(profitAnalysis.row3_2, true, true)}
                </EBValue>
              </EBRow>

              <EBRow>
                <EBLabel>질권 대출</EBLabel>
                <EBValue>{ReportFormatNumber(profitAnalysis.row2_2)}</EBValue>
              </EBRow>

              <EBRow>
                <EBLabel>이자비용</EBLabel>
                <EBValue>{ReportFormatNumber(profitAnalysis.row4_3)}</EBValue>
                <EBSubRight>질권대출 * 이자율 * 경매기간</EBSubRight>
              </EBRow>

              <EBRow>
                <EBLabel>질권 취급수수료</EBLabel>
                <EBValue>{ReportFormatNumber(profitAnalysis.row6_2)}</EBValue>
                <EBSubRight>질권대출 * 0.5%</EBSubRight>
              </EBRow>
            </EBList>
          </ToggleSection>

          <ToggleSection
            label="부대비용 소계"
            amount={ReportFormatNumber(extraSubtotalB)}
            isOpen={openExtraB}
            onToggle={() => setOpenExtraB((v) => !v)}
          >
            <EBList>
              <EBRow>
                <EBLabel>질권인지세</EBLabel>
                <EBValue>{ReportFormatNumber(profitAnalysis.row5_2)}</EBValue>
              </EBRow>

              <EBRow>
                <EBLabel>거래수수료</EBLabel>
                <EBValue>
                  {ReportFormatNumber(
                    Math.round(profitAnalysis.row5_1 * 0.015),
                  )}
                </EBValue>
                <EBSubRight>채권 매입금액의 1.5%</EBSubRight>
              </EBRow>

              <EBRow>
                <EBLabel>근저당권 이전수수료</EBLabel>
                <EBValue>{ReportFormatNumber(profitAnalysis.row7_1)}</EBValue>
                <EBSubRight>채권 매입금액의 0.5%</EBSubRight>
              </EBRow>
            </EBList>
          </ToggleSection>

          <ToggleSection
            label="경매비용"
            amount={ReportFormatNumber(profitAnalysis.row9)}
            isOpen={openAuctionB}
            onToggle={() => setOpenAuctionB((v) => !v)}
          >
            <EBList>
              <EBRow>
                <EBLabel>경매비용</EBLabel>
                <EBValue>{ReportFormatNumber(profitAnalysis.row9)}</EBValue>
                <EBSubRight>예상 낙찰가의 0.5%</EBSubRight>
              </EBRow>
            </EBList>
          </ToggleSection>
        </ToggleList>
        <s.Description style={{ marginTop: '10px' }}>
          <img
            src="/assets/images/icon/info_16.svg"
            alt=""
            style={{ width: 16 }}
          />
          <span>부실채권 할인금액은 할인율에 따라 변동될 수 있습니다.</span>
        </s.Description>
        <s.Description>
          <img
            src="/assets/images/icon/info_16.svg"
            alt=""
            style={{ width: 16 }}
          />
          <span>비용내역은 요율에 따라 변경될 수 있습니다.</span>
        </s.Description>
      </Subdiv>
    </s.Div>
  );
};

export default CostAnalysis;

const Subtitle = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  font-size: 14px;
  color: #222;
  font-weight: 600;
  align-items: center;
`;

const Subdiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`;

const ToggleList = styled.div`
  margin-top: 8px;
`;

const Toggle = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #eee;
`;

const RowButton = styled.button`
  width: 100%;
  padding: 12px 0;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const RowLabel = styled.span`
  font-size: 13px;
  color: #616161;
  font-weight: 500;
`;

const RowRight = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const RowAmount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #222;
`;

const Arrow = styled.img`
  width: 17px;
  height: 17px;
`;

const ExpandBox = styled.div`
  padding-left: 12px;
  margin-bottom: 24px;
  color: rgba(55, 56, 60, 0.61);
  font-size: 12px;
  font-weight: 500;
  border-left: 3px solid #f3f5f7;
`;
